import { useState } from 'react';
import { format, isSameMonth } from 'date-fns';
import { ChevronDown, ChevronLeft, ChevronRight } from 'lucide-react';
import { IconTooltipButton } from '@/components/common/IconTooltipButton';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { ACCENT_MARK_CLASS } from '@/styles/accentMark';
import { CALENDAR_POPOVER_CLASS } from '@/styles/floatingPanel';

const MONTHS_IN_YEAR = 12;
const SHORT_MONTH_FORMAT = 'MMM';
const MONTH_INDEXES = Array.from({ length: MONTHS_IN_YEAR }, (_, monthIndex) => monthIndex);

interface HolidayMonthPickerProps {
  monthStart: Date;
  monthLabel: string;
  onChangeMonth: (monthStart: Date) => void;
}

export function HolidayMonthPicker({
  monthStart,
  monthLabel,
  onChangeMonth,
}: HolidayMonthPickerProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [shownYear, setShownYear] = useState(monthStart.getFullYear());
  const today = new Date();

  function handleOpenChange(nextIsOpen: boolean) {
    if (nextIsOpen) setShownYear(monthStart.getFullYear());
    setIsOpen(nextIsOpen);
  }

  function pickMonth(monthIndex: number) {
    onChangeMonth(new Date(shownYear, monthIndex, 1));
    setIsOpen(false);
  }

  return (
    <Popover open={isOpen} onOpenChange={handleOpenChange}>
      <PopoverTrigger
        aria-label={`Pick a month, now ${monthLabel}`}
        className="inline-flex h-7 min-w-36 cursor-pointer items-center justify-center gap-1 rounded-lg px-2 text-sm font-semibold tabular-nums transition-colors outline-none hover:bg-primary/6 focus-visible:ring-2 focus-visible:ring-ring aria-expanded:bg-primary/8"
      >
        {monthLabel}
        <ChevronDown className="size-3.5 text-muted-foreground" aria-hidden />
      </PopoverTrigger>
      <PopoverContent className={cn(CALENDAR_POPOVER_CLASS, 'w-64 p-3')} align="center">
        <div className="flex items-center justify-between">
          <IconTooltipButton
            icon={ChevronLeft}
            label="Previous year"
            onClick={() => setShownYear((year) => year - 1)}
          />
          <span className="text-sm font-semibold tabular-nums">{shownYear}</span>
          <IconTooltipButton
            icon={ChevronRight}
            label="Next year"
            onClick={() => setShownYear((year) => year + 1)}
          />
        </div>
        <div className="mt-2 grid grid-cols-3 gap-1.5">
          {MONTH_INDEXES.map((monthIndex) => {
            const month = new Date(shownYear, monthIndex, 1);
            const isShown = isSameMonth(month, monthStart);
            const isCurrent = isSameMonth(month, today);
            return (
              <button
                key={monthIndex}
                type="button"
                onClick={() => pickMonth(monthIndex)}
                aria-pressed={isShown}
                className={cn(
                  'h-9 cursor-pointer rounded-lg text-sm font-medium transition-colors focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none',
                  isShown ? ACCENT_MARK_CLASS : 'hover:bg-primary/8',
                  isCurrent && !isShown && 'text-primary ring-1 ring-primary/40',
                )}
              >
                {format(month, SHORT_MONTH_FORMAT)}
              </button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}
